<template>
  <div
    class="lang-wrap"
    :class="isDark ? 'is-dark' : 'is-light'"
    @keydown.esc="showDropdown = false"
    ref="wrap"
  >
    <!-- Trigger -->
    <button
      type="button"
      class="lang-btn"
      :aria-expanded="showDropdown ? 'true' : 'false'"
      :title="$t('language.select')"
      @click.stop="toggle"
    >
      <span class="flag" v-if="currentLanguage.icon">
        <img :src="currentLanguage.icon" :alt="currentLanguage.name" />
      </span>
      <span class="label">{{ currentLanguage.name }}</span>
      <span class="chev" aria-hidden="true">▾</span>
    </button>

    <!-- Dropdown -->
    <transition name="fade-scale">
      <div
        v-show="showDropdown"
        class="menu"
        role="menu"
        @click.stop
      >
        <div
          v-for="language in availableLanguages"
          :key="language.locale"
          class="item"
          :class="{ active: language.locale === currentLanguage.locale }"
          role="menuitem"
          tabindex="0"
          @click="selectLanguage(language)"
          @keydown.enter.prevent="selectLanguage(language)"
        >
          <span class="flag" v-if="language.icon">
            <img :src="language.icon" :alt="language.name" />
          </span>
          <span class="text">{{ language.name }}</span>
          <span class="tick" v-if="language.locale === currentLanguage.locale">✔</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'LanguageSelector',
  data() {
    return { showDropdown: false }
  },
  computed: {
    isDark() {
      return this.$store.state.ui.dark
    },
    currentLanguage() {
      return this.$store.state.ui.language || { locale: 'en', name: 'English' }
    },
    availableLanguages() {
      return this.$store.state.ui.languages || []
    }
  },
  mounted() {
    document.addEventListener('click', this.onOutside)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onOutside)
  },
  methods: {
    toggle() {
      this.showDropdown = !this.showDropdown
    },
    selectLanguage(language) {
      this.showDropdown = false
      this.$store.dispatch('LOCALIZE', language)
    },
    onOutside(e) {
      if (!this.$refs.wrap) return
      if (!this.$refs.wrap.contains(e.target)) this.showDropdown = false
    }
  }
}
</script>

<style scoped>
/* --- Container --- */
.lang-wrap {
  position: relative;
  display: inline-block;
  font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji";
}

/* Preset biến theo theme */
.lang-wrap.is-dark {
  --bg: #1f2430;
  --bg-hover: #252b3a;
  --bd: #2b3040;
  --bd-hover: #3a4156;
  --text: #ffffff;

  --menu-bg: #1f2430;
  --menu-bd: #2b3040;
  --menu-shadow: 0 10px 30px rgba(0,0,0,.35);
  --active-bg: #2a3142;

  --focus: #3a7afe55;
}

.lang-wrap.is-light {
  --bg: #f3f4f6;        /* pill sáng */
  --bg-hover: #e9edf3;
  --bd: #d5d9e0;
  --bd-hover: #c8cdd6;
  --text: #111827;

  --menu-bg: #ffffff;   /* dropdown sáng */
  --menu-bd: #e5e7eb;
  --menu-shadow: 0 12px 24px rgba(17,24,39,.10);
  --active-bg: #eef2f7;

  --focus: #3a7afe55;
}

/* --- Trigger button (pill) --- */
.lang-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  height: 32px;
  background: var(--bg);
  color: var(--text);
  border: 1px solid var(--bd);
  border-radius: 10px;     /* pill nhẹ */
  cursor: pointer;
  line-height: 1;
  transition: background .15s, border-color .15s, transform .02s, box-shadow .15s;
}
.lang-btn:hover { background: var(--bg-hover); border-color: var(--bd-hover); }
.lang-btn:active { transform: translateY(1px); }
.lang-btn:focus { outline: 2px solid var(--focus); outline-offset: 2px; }

/* flag + text + caret */
.flag { 
  width: 18px; 
  height: 18px; 
  display: inline-flex; 
  align-items: center;
  justify-content: center;
  border-radius: 2px; 
  overflow: hidden; 
  line-height: 1;
  flex-shrink: 0;
}
.flag img { width: 100%; height: 100%; display: block; }
.label {
  font-size: 14px;
  white-space: nowrap;
  font-weight: 600;
  line-height: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  margin-bottom: 0 !important;
  .is-dark & {
    color: #ffffff;
  }
}
.chev { 
  font-size: 12px; 
  opacity: .7; 
  margin-left: 2px; 
  line-height: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
}

/* --- Dropdown menu --- */
.menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 180px;
  background: var(--menu-bg);
  border: 1px solid var(--menu-bd);
  border-radius: 10px;
  box-shadow: var(--menu-shadow);
  padding: 6px;
  z-index: 9999;
}

/* Item row */
.item {
  display: grid;
  grid-template-columns: 18px 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  color: var(--text);
  cursor: pointer;
  transition: background .12s, color .12s, border-color .12s;
}
.item:hover { background: var(--active-bg); }
.item.active {
  background: var(--active-bg);
  border: 1px solid var(--bd-hover);
}
.item .text { font-size: 14px; }

/* check mark */
.tick { font-size: 12px; opacity: .9; }

/* --- Transition --- */
.fade-scale-enter-active,
.fade-scale-leave-active { transition: opacity .12s ease, transform .12s ease; }
.fade-scale-enter, .fade-scale-leave-to { opacity: 0; transform: scale(.98); }
</style>
